import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/Primitives";
import { Loader2, ArrowRight } from "lucide-react";

export default function CampaignsList() {
  const [items, setItems] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [advFilter, setAdvFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: a }] = await Promise.all([
          api.get("/campaigns/"),
          api.get("/advertisers/"),
        ]);
        setItems(c);
        setAdvertisers(a);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => items.filter((c) => {
    if (advFilter !== "all" && c.advertiser_id !== advFilter) return false;
    if (statusFilter !== "all" && c.push_status !== statusFilter) return false;
    return true;
  }), [items, advFilter, statusFilter]);

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1400px]">
      <PageHeader
        eyebrow="Module / 05"
        title="Campaigns"
        description="Every push to the external campaign platform, with status, external ID, and last-synced report timestamp."
        testid="campaigns-list-header"
      />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <select
          data-testid="campaigns-advertiser-filter"
          value={advFilter}
          onChange={(e) => setAdvFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
        >
          <option value="all">All advertisers</option>
          {advertisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select
          data-testid="campaigns-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
        >
          <option value="all">All statuses</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        <p className="text-xs font-mono uppercase tracking-[0.15em] text-[#555] self-center ml-auto">
          {filtered.length} of {items.length} campaigns
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-[#555] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[#E0E0D8] bg-white p-16 text-center">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#555]">
            {items.length === 0 ? "No campaigns yet" : "No campaigns match these filters"}
          </p>
          {items.length === 0 && (
            <Link to="/campaigns/new" className="inline-block mt-4 bg-[#FF3E00] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D93500]">
              Build your first campaign
            </Link>
          )}
        </div>
      ) : (
        <div className="border border-[#E0E0D8] bg-white overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A]">
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Name</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Advertiser</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Objective</th>
                <th className="text-right py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Budget</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">External ID</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Status</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Last sync</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} data-testid={`campaign-row-${c.id}`} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0]">
                  <td className="py-3 px-4 text-sm font-medium">{c.name}</td>
                  <td className="py-3 px-4 text-sm">{c.advertiser}</td>
                  <td className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-[#555]">{c.objective}</td>
                  <td className="py-3 px-4 text-right font-mono text-sm">${c.budget_usd.toLocaleString()}</td>
                  <td className="py-3 px-4 font-mono text-xs">{c.external_campaign_id || "—"}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
                      c.push_status === "accepted" ? "text-[#00994D] border-[#00994D]/30 bg-[#00994D]/10" :
                                                     "text-[#D91919] border-[#D91919]/30 bg-[#D91919]/10"
                    }`}>{c.push_status}</span>
                  </td>
                  <td className="py-3 px-4 font-mono text-[11px] text-[#555]">
                    {c.last_report_synced_at ? new Date(c.last_report_synced_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-3 px-4">
                    <Link to={`/campaigns/${c.id}`} className="text-[11px] font-mono uppercase tracking-wider text-[#FF3E00] inline-flex items-center gap-1 hover:underline">
                      Open <ArrowRight size={11} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
